import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { HardHat, Calculator, Layers, RefreshCcw, FileSpreadsheet, ShieldCheck, Loader2 } from 'lucide-react'; // eslint-disable-line no-unused-vars
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { useAuth } from '../context/AuthContext';
import { errMsg } from '../lib/format';

const FEATURES = [
  { icon: Calculator, text: 'Análisis de Precio Unitario (APU) con cascada de indirectos, financiamiento y utilidad' },
  { icon: Layers, text: 'Módulos Prefab, Fotovoltaico y Contenedores con catálogos aislados por scope' },
  { icon: RefreshCcw, text: 'Propagación automática de precios de catálogo a cotizaciones en borrador' },
  { icon: FileSpreadsheet, text: 'Multi-moneda MXN/USD con tipo de cambio Banxico y exportación PDF/Excel' },
  { icon: ShieldCheck, text: 'Roles y permisos: administrador, editor y capturista' },
];

export default function LoginPage() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    if (user) navigate('/', { replace: true });
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) return;
    setLoading(true);
    try {
      await login(email, password);
      toast.success('Bienvenido a CotizaBuilder');
      navigate('/', { replace: true });
    } catch (error) {
      toast.error(errMsg(error, 'No se pudo iniciar sesión'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* Panel de marca */}
      <div className="hidden lg:flex relative flex-col justify-between p-12 overflow-hidden border-r border-border">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "url('/assets/hero.jpg')" }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/80 to-background/40" />
        <div className="relative z-10 flex items-center gap-3">
          <img src="/assets/logo.png" alt="Logotipo" className="h-12 w-auto" data-testid="login-logo" />
          <div>
            <div className="text-xl font-semibold" style={{ fontFamily: 'Space Grotesk' }}>CotizaBuilder</div>
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Cotización de construcción · APU</div>
          </div>
        </div>
        <div className="relative z-10 space-y-6 max-w-lg">
          <h2 className="text-3xl font-semibold leading-tight">
            Cotiza obra con precisión de ingeniería de costos
          </h2>
          <ul className="space-y-4">
            {FEATURES.map(({ icon: Icon, text }, i) => (
              <li key={i} className="flex items-start gap-3 text-sm text-muted-foreground">
                <span className="mt-0.5 h-7 w-7 shrink-0 rounded-md bg-primary/10 border border-primary/25 flex items-center justify-center">
                  <Icon size={14} className="text-primary" />
                </span>
                {text}
              </li>
            ))}
          </ul>
        </div>
        <div className="relative z-10 text-xs text-muted-foreground">PREFABCON · México</div>
      </div>

      {/* Tarjeta de login */}
      <div className="flex items-center justify-center p-6">
        <Card className="w-full max-w-md border-border shadow-[var(--shadow-elev-1)]" data-testid="login-card">
          <CardHeader className="space-y-1">
            <div className="lg:hidden flex items-center gap-2 mb-3">
              <img src="/assets/logo.png" alt="Logotipo" className="h-8 w-auto" />
              <span className="font-semibold text-lg" style={{ fontFamily: 'Space Grotesk' }}>CotizaBuilder</span>
            </div>
            <CardTitle className="text-xl">Iniciar sesión</CardTitle>
            <CardDescription>Ingresa tus credenciales para acceder al sistema</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="email">Correo electrónico</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="correo@empresa.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="login-email-input"
                  autoComplete="email"
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="password">Contraseña</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="login-password-input"
                  autoComplete="current-password"
                  required
                />
              </div>
              <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit-button">
                {loading ? <Loader2 className="animate-spin" size={16} /> : 'Entrar'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
